import { useState } from "react";
import { Card } from "@/ui-components/ui/card";
import { Button } from "@/ui-components/ui/button";
import { Check, Copy, Terminal, ArrowRight } from "lucide-react";
import { useCLIAuthToken } from "@/hooks/useCLIAuthToken";
import { AppHeader } from "../header/AppHeader";
import { BackgroundGradient } from "@/components/common/BackgroundGradient";

// Calculate relative time until expiration
function getRelativeTime(expiresAt: string): string {
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffMs = expiry.getTime() - now.getTime();

  if (diffMs <= 0) {
    return "expired";
  }

  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffDays > 0) {
    return `${diffDays} day${diffDays > 1 ? "s" : ""}`;
  } else if (diffHours > 0) {
    return `${diffHours} hour${diffHours > 1 ? "s" : ""}`;
  } else if (diffMins > 0) {
    return `${diffMins} minute${diffMins > 1 ? "s" : ""}`;
  } else {
    return "less than a minute";
  }
}

function formatCode(code: string): string {
  // We have 6 chars, fill a dash after every 3 chars
  return code.replace(/(\w{3})(\w{3})/, "$1-$2").trim();
}

export default function CLILoginPage() {
  const cliProps = useCLIAuthToken();
  const { code, success, expiresAt, loading, error } = cliProps;
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null);

  const copyToClipboard = async (text: string, commandId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCommand(commandId);
      setTimeout(() => setCopiedCommand(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleComplete = () => {
    // Navigate to dashboard
    window.location.href = "/";
  };

  const relativeTime = expiresAt ? getRelativeTime(expiresAt) : "24 hours";
  const isExpired = relativeTime === "expired";

  const authCodeDisplay = () => {
    if (code) {
      return (
        <div className="bg-[#D97757]/10 rounded-lg p-5 text-center">
          <p className="text-sm text-muted-foreground mb-2">
            Copy this code (expires in {relativeTime}):
          </p>
          <div className="flex items-center justify-center gap-3">
            <code className="text-2xl font-mono font-bold text-foreground">
              {formatCode(code)}
            </code>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => copyToClipboard(formatCode(code), "authcode")}
              className="h-8 w-8 p-0 hover:bg-muted"
            >
              {copiedCommand === "authcode" ? (
                <Check className="w-4 h-4 text-green-600" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="h-full flex flex-col">
      <AppHeader />
      <div className="flex-1 min-h-0"></div>
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <BackgroundGradient />

        <div className="relative w-full max-w-lg">
          {/* Logo Connection */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-xl bg-[#D97757] flex items-center justify-center shadow-md">
                <img
                  src="/assets/claude.svg"
                  alt="Claude"
                  className="w-10 h-10 filter brightness-0 invert"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-muted" />
                <div className="w-2 h-2 rounded-full bg-muted" />
                <div className="w-2 h-2 rounded-full bg-muted" />
              </div>
              <div className="w-14 h-14 rounded-xl bg-muted border border-border flex items-center justify-center">
                <img
                  src="/assets/SpecStory-logo.png"
                  alt="SpecStory"
                  className="w-10 h-10"
                />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-foreground">
              Connect SpecStory CLI to SpecStory Cloud
            </h1>
          </div>

          {/* Main Card */}
          <Card className="p-6 bg-card border-border shadow-lg">
            <h2 className="font-semibold text-foreground mb-4 text-lg">
              Complete your CLI setup
            </h2>

            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Your terminal is waiting for an authorization code.
              </p>

              {loading ? (
                <div className="bg-[#D97757]/10 rounded-lg p-5 text-center">
                  <p className="text-sm text-muted-foreground mb-2">
                    Generating authentication code...
                  </p>
                </div>
              ) : isExpired ? (
                <div className="bg-[#D97757]/10 rounded-lg p-5 text-center">
                  <h1 className="mb-2">❌ Code is expired</h1>
                  <button
                    onClick={() => window.location.reload()}
                    className="cli-login-refresh-btn"
                  >
                    🔄 Get New Code
                  </button>
                </div>
              ) : success ? (
                authCodeDisplay()
              ) : (
                <div className="bg-[#D97757]/10 rounded-lg p-5 text-center">
                  <h1 className="mb-2">❌ Error</h1>
                  <p className="text-sm text-muted-foreground mb-2">{error}</p>
                </div>
              )}

              {/* What happens next */}
              <Card className="p-3 bg-[#D97757]/5 border-[#D97757]/20">
                <div className="flex items-start gap-2">
                  <Terminal className="w-4 h-4 text-[#D97757] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-foreground mb-1">
                      What happens next:
                    </p>
                    <p className="text-sm text-muted-foreground">
                      After pasting the code, the CLI will authenticate and show
                      you how to sync projects using{" "}
                      <code className="bg-card px-1.5 py-0.5 rounded text-xs font-mono">
                        specstory sync
                      </code>
                    </p>
                  </div>
                </div>
              </Card>

              {/* Complete Button */}
              <div className="pt-2">
                <Button
                  onClick={handleComplete}
                  className="w-full bg-[#D97757] hover:bg-[#c26644] text-white rounded-lg py-2.5 font-medium transition-all duration-300"
                >
                  I've entered the code
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
